import {
  Badge,
  Pill,
  Tile,
  TileContent,
  TileMedia,
  TileText,
  TileTitle,
} from '@design-system';
import Link from 'next/link';

import { PLACE_HOLDER_IMAGE } from '../../constants';
import { Ratio } from '../../helpers/images';
import { AssetStoryblok } from '../../types/generated-storyblok';
import { Image } from '../Image';
import { PublishedAt, ReadingTime } from '../Posts';

export const JournalsTile = ({
  description,
  eagerLoading = false,
  href,
  image,
  imageRatio = '4/3',
  mediaDuration,
  onClick,
  publishedAt,
  readingTime,
  tag,
  title,
}: {
  description: string;
  eagerLoading?: boolean;
  href: string;
  image: AssetStoryblok;
  imageRatio?: Ratio;
  mediaDuration?: string;
  onClick: () => void;
  publishedAt: string;
  readingTime: number;
  tag?: string;
  title: string;
}) => (
  <Tile className="group" direction="column">
    <TileMedia overlay={mediaDuration ? <Badge label={mediaDuration} /> : null}>
      <Image
        alt={image?.alt || PLACE_HOLDER_IMAGE}
        className="rounded-sm"
        eagerLoading={eagerLoading}
        focus={image?.focus}
        hoverEffect
        size={{
          sm: { ratio: imageRatio, span: 12 },
          md: { ratio: imageRatio, span: 4 },
          lg: { ratio: imageRatio, span: 3 },
        }}
        src={image?.filename || PLACE_HOLDER_IMAGE}
      />
    </TileMedia>

    {tag ? (
      <TileContent className="w-full items-start" direction="column">
        <Pill label={tag} />
      </TileContent>
    ) : null}

    <TileContent className="w-full" direction="column">
      <TileTitle as="h3" className="text-md">
        <Link
          href={href}
          onClick={onClick}
          prefetch={false}
          className="after:absolute after:inset-0 after:block after:content-['']"
        >
          {title}
        </Link>
      </TileTitle>

      <TileText as="p" className="line-clamp-3">
        {description}
      </TileText>

      <TileText variant="muted" className="mt-3">
        <PublishedAt date={publishedAt} />
        {readingTime > 1 && (
          <>
            <span aria-hidden className="mx-1 before:content-['·']" />
            <ReadingTime minutes={String(readingTime)} />
          </>
        )}
      </TileText>
    </TileContent>
  </Tile>
);
